import { RevealGroup, RevealItem } from '@/components/Reveal';
import Typography from '@/components/ui/typography';
import { cn } from '@/lib/utils';

export interface TimelineEntry {
  title: string;
  detail: string;
}

export interface TimelineGroup {
  /** The year, or year range, this group sits under. */
  marker: string;
  entries: TimelineEntry[];
}

/*
 * The dated timelines — awards, and the Rotary offices held.
 *
 * These are the two About sections with no photographs behind them, so the
 * years themselves have to supply the scale and rhythm the images give the
 * chapter pages. Set large in the display face, in their own column, they do:
 * scrolling the section becomes scrolling the years.
 *
 * This replaces the chapter spine's circular marker, which could not hold a
 * four-digit year at any readable size — "2024-25" was down to 9px inside a
 * 32px disc.
 */
const Timeline = ({
  groups,
  unit,
}: {
  groups: TimelineGroup[];
  /** Singular and plural for the count beneath each year. */
  unit: [string, string];
}) => (
  <ol className="flex flex-col">
    {groups.map((group) => {
      // A year range needs roughly twice the width of a year, and the column is
      // fixed — so it steps down rather than overflowing it.
      const wide = group.marker.length > 4;

      return (
        <li
          key={group.marker}
          className="grid gap-3 pb-12 last:pb-0 md:grid-cols-[10rem_1fr] md:gap-10 md:pb-16"
        >
          {/* Sticky so the year holds in place while its entries scroll past,
              which is what keeps a long list anchored to a date. */}
          <div className="flex items-baseline gap-3 md:sticky md:top-28 md:h-fit md:flex-col md:items-start md:gap-1">
            <span
              className={cn(
                'font-display font-semibold tabular-nums tracking-tight text-foreground',
                wide
                  ? 'text-2xl md:text-3xl lg:text-4xl'
                  : 'text-4xl md:text-5xl lg:text-6xl'
              )}
            >
              {group.marker}
            </span>
            <Typography.Small>
              {group.entries.length}{' '}
              {group.entries.length === 1 ? unit[0] : unit[1]}
            </Typography.Small>
          </div>

          <div className="relative md:pl-10">
            {/* One rail per year, beside the entries rather than running the
                whole section — unlike the chapter spine's, which stacked into a
                column of lines down the page and was removed for it. Hidden on
                mobile, where the year stacks above instead of beside. */}
            <div
              aria-hidden
              className="seam-vertical absolute bottom-0 left-0 top-1.5 hidden md:block"
            />

            <RevealGroup className="flex flex-col">
              {group.entries.map((entry, index) => (
                <RevealItem key={`${entry.title}-${entry.detail}`}>
                  {index > 0 && <div aria-hidden className="seam my-5" />}

                  <div className="flex flex-col gap-1">
                    <Typography.Display3 className="text-lg md:text-xl lg:text-2xl">
                      {entry.title}
                    </Typography.Display3>
                    <Typography.Subtle>{entry.detail}</Typography.Subtle>
                  </div>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </li>
      );
    })}
  </ol>
);

export default Timeline;
